import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import { MessageCircle, Eye, MousePointerClick, Copy, Trash2, ExternalLink, Search, IndianRupee } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";

export default function CatalogSharesPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const canEdit = can(user, "catalog.share");

  const load = async () => {
    try {
      const params = mineOnly ? { mine_only: true } : {};
      const { data } = await api.get("/catalog-shares", { params });
      setRows(data || []);
    } catch (e) { toast.error("Failed to load shares"); }
  };
  useEffect(() => { load(); }, [mineOnly]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r =>
      (r.title || "").toLowerCase().includes(s) ||
      (r.contact_name || "").toLowerCase().includes(s) ||
      (r.whatsapp_number || "").includes(s) ||
      (r.created_by_name || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const copy = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/c/${token}`);
    toast.success("Catalog link copied");
  };
  const openWA = (share) => {
    const link = `${window.location.origin}/c/${share.share_token}`;
    const msg = encodeURIComponent(`*${share.title}*\n\nView catalog: ${link}`);
    const phone = (share.whatsapp_number || "").replace(/[^\d]/g, "");
    window.open(phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank");
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this share? The public link will stop working.")) return;
    try { await api.delete(`/catalog-shares/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const totalViews = filtered.reduce((s, r) => s + (r.view_count || 0), 0);
  const totalEnq = filtered.reduce((s, r) => s + (r.enquiry_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="catalog-shares-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">WhatsApp</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">Catalog Shares</h1>
        <p className="text-[#4A5A50] mt-1">Every catalog you've sent — with live view & enquiry counts.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatTile label="Shares sent" value={filtered.length} icon={MessageCircle} tint="text-[#0F5E39]"/>
        <StatTile label="Total views" value={totalViews} icon={Eye} tint="text-[#0468A6]"/>
        <StatTile label="Enquiries" value={totalEnq} icon={MousePointerClick} tint="text-[#6E5416]"/>
      </div>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5A50]"/>
              <Input placeholder="Search by title, contact or sender…" className="pl-9" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="shares-search"/>
            </div>
            <label className="text-sm text-[#4A5A50] inline-flex items-center gap-2">
              <input type="checkbox" checked={mineOnly} onChange={(e)=>setMineOnly(e.target.checked)} data-testid="shares-mine-only"/>
              Only mine
            </label>
          </div>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="py-16 text-center">
            <MessageCircle size={32} className="text-[#25D366] mx-auto mb-3" strokeWidth={1.5}/>
            <div className="font-serif text-xl">No shares yet</div>
            <div className="text-[#4A5A50] text-sm mt-1">Head to Products, pick a few pieces, and hit "Share on WhatsApp".</div>
            <Button className="mt-4 rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => nav("/products")} data-testid="shares-empty-cta">
              Open Products
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <Card key={r.id} className="border-[#E4DFCF] rounded-xl card-hover" data-testid={`share-row-${r.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50]">
                      {format(new Date(r.created_at), "dd MMM yyyy · HH:mm")} · by {r.created_by_name || "someone"}
                    </div>
                    <div className="font-serif text-xl mt-1">{r.title}</div>
                    {r.contact_name && <div className="text-sm text-[#4A5A50] mt-0.5">→ {r.contact_name}{r.whatsapp_number ? ` · ${r.whatsapp_number}` : ""}</div>}
                    {!r.contact_name && r.whatsapp_number && <div className="text-sm text-[#4A5A50] mt-0.5">→ {r.whatsapp_number}</div>}
                    {r.note && <div className="text-sm text-[#4A5A50] mt-2 italic line-clamp-2">"{r.note}"</div>}
                    <div className="mt-3 flex flex-wrap gap-3 text-xs">
                      <span className="text-[#4A5A50]"><strong>{r.product_ids?.length || 0}</strong> items</span>
                      <span className="text-[#0468A6] inline-flex items-center gap-1"><Eye size={12}/> <strong>{r.view_count || 0}</strong> views</span>
                      <span className="text-[#6E5416] inline-flex items-center gap-1"><MousePointerClick size={12}/> <strong>{r.enquiry_count || 0}</strong> enquiries</span>
                      {r.last_viewed_at && <span className="text-[#4A5A50]">Last viewed {format(new Date(r.last_viewed_at), "dd MMM")}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => window.open(`/c/${r.share_token}`, "_blank")} className="rounded-full" data-testid={`share-open-page-${r.id}`}>
                      <ExternalLink size={12} className="mr-1"/> Open
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copy(r.share_token)} className="rounded-full" data-testid={`share-copy-${r.id}`}>
                      <Copy size={12} className="mr-1"/> Copy link
                    </Button>
                    <Button size="sm" onClick={() => openWA(r)} className="rounded-full bg-[#25D366] hover:bg-[#1FAE55] text-white" data-testid={`share-wa-${r.id}`}>
                      <MessageCircle size={12} className="mr-1"/> Resend
                    </Button>
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-red-600 hover:bg-red-50 rounded-full" data-testid={`share-del-${r.id}`}>
                        <Trash2 size={12}/>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tint }) {
  return (
    <Card className="border-[#E4DFCF] rounded-xl">
      <CardContent className="p-5 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{label}</div>
          <div className="font-serif text-3xl mt-1">{value}</div>
        </div>
        <Icon size={28} className={tint} strokeWidth={1.2}/>
      </CardContent>
    </Card>
  );
}
